import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ShortcutBar from '@/components/feature/ShortcutBar';
import BillingTabBar from '@/pages/billing/components/BillingTabBar';
import PrintSalesInvoice from '@/components/print/PrintSalesInvoice';
import ItemForm, { type ItemFormData } from '@/pages/masters/items/components/ItemForm';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useWarehouseStore } from '@/stores/warehouseStore';
import {
  createEmptySalesInvoiceDraft,
  useBillingTabStore,
  type SalesCustomerDraft,
  type SalesInvoiceDraft,
  type SalesPaymentBreakdown,
} from '@/stores/billingTabStore';

import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/contexts/ToastContext';
import { isSameStateCheck, calcGST, round2 } from '@/utils/gst';
import { formatINR } from '@/utils/format';
import { calcTotals, emptyLineItem, type InvoiceLineItem } from '@/types/billing';
import { isBackendAvailable } from '@/api/client';
import { salesService } from '@/services/salesService';
import type { SalesInvoiceDetail } from '@/services/salesService';
import { getNextDocumentNumber } from '@/services/documentNumberService';
import { printSalesInvoice } from '@/utils/printDocument';
import { createItem, filterItems, mapApiToItem, type ItemResponse } from '@/api/item.api';
import { getPartyById, fromDBType, type PartyResponse } from '@/api/party.api';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { categoryService } from '@/services/categoryService';
import { complianceApi } from '@/api/compliance.api';
import InvoiceItemsTable from './components/InvoiceItemsTable';
import PaymentSection, { type PaymentBreakdown } from './components/PaymentSection';
import CustomerSection, { type CustomerInfo } from './components/CustomerSection';
import InvoiceInfoSummary from './components/InvoiceInfoSummary';
import { useCompanyState } from '@/hooks/useCompanyState';
import { useUnits, carryLineUnit } from '@/hooks/useUnits';
type EditInvoiceState = SalesInvoiceDetail;

let invoiceCounter = 8;


const nextTempInvoiceNo = () => `INV-${String(invoiceCounter++).padStart(4, '0')}`;

// Indian financial year runs April→March — display only, the server recomputes and
// persists its own authoritative value on save (mirrors the backend helper).
function getFinancialYear(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.getMonth();
  const year = d.getFullYear();
  const fyStart = month >= 3 ? year : year - 1;
  return `${fyStart}-${String(fyStart + 1).slice(-2)}`;
}

const emptyPayment = (): PaymentBreakdown => ({
  cash: 0, upi: 0, card: 0, cheque: 0,
  chequeBankName: '', chequeNo: '', chequeDate: '', chequeBranch: '',
});

interface OverstockItem {
  rowIdx: number;
  itemName: string;
  entered: number;
  available: number;
}

interface ItemLookup {
  id: string;
  name: string;
  code: string;
  hsnCode: string;
  taxRate: number;
  purchaseRate: number;
  saleRate: number;
  unit: string;
  unitId: string;
  categoryId?: string;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
}

interface CategoryLookup {
  id: string;
  name: string;
  requiresNarration: boolean;
}

interface WarehouseLookup {
  id: string;
  name: string;
}

export default function SalesInvoiceNewPage() {
  const navigate = useNavigate();
  const units = useUnits();
  const toast = useToast();
  const companyStateCode = useCompanyState();
  const user = useAuthStore((s) => s.user);
  const { tabs, activeTabId, addTab, setActiveTab, closeTab, setTabDraft, markSaved } = useBillingTabStore();
  const hydratingRef = useRef(false);
  const initTabRef = useRef(false);
  // Blocks the auto-save effect from writing stale state back to the store
  // right after a successful save (before the component unmounts/navigates).
  const savedAndNavigatingRef = useRef(false);
  const location = useLocation();
  const editInvoice = (location?.state?.data as EditInvoiceState | undefined) ?? null;
  const fromProformaPrefill = (location?.state?.fromProforma as any) ?? null;
  const fromSalesOrderPrefill = (location?.state?.fromSalesOrder as any) ?? null;
  const fromQuotationPrefill = (location?.state?.fromQuotation as any) ?? null;
  const orderCustomerHydratedRef = useRef<string | null>(null);
  const duplicateCustomerHydratedRef = useRef<string | null>(null);
  const challanCustomerHydratedRef = useRef<string | null>(null);
  const editCustomerHydratedRef = useRef<string | null>(null);
  const editInvoiceItemsHydratedRef = useRef(false);
  useEffect(() => {
    if (initTabRef.current) return; // Guard against double initialization in strict mode
    initTabRef.current = true;
    if (tabs.length === 0) {
      addTab('SALE');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTab = tabs.find((t) => t.id === activeTabId && t.voucherType === 'SALE');
  const challanPrefill = activeTab?.fromChallan ?? null;
  const duplicatePrefill = activeTab?.fromDuplicate ?? null;
  const activeDraft = activeTab?.salesInvoiceDraft ?? createEmptySalesInvoiceDraft();

  const [invoiceNo, setInvoiceNo] = useState(() => activeDraft?.invoiceNo || nextTempInvoiceNo());
  const [date, setDate] = useState(() => activeDraft?.date || new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(() => activeDraft?.dueDate || '');
  const [salesman, setSalesman] = useState('');
  const { selectedWarehouseId: warehouseId, selectedWarehouseName, selectedWarehouseGstin, selectedWarehouseAddress } = useWarehouseStore();
  const [warehouseLookup, setWarehouseLookup] = useState<WarehouseLookup[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerInfo | null>(null);
  const [billingAddress, setBillingAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [payment, setPayment] = useState<PaymentBreakdown>(emptyPayment());
  const [narration, setNarration] = useState('');
  const [items, setItems] = useState<InvoiceLineItem[]>([emptyLineItem()]);
  const [saving, setSaving] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [quickAddIdx, setQuickAddIdx] = useState<number | null>(null);
  const [creditSkipped, setCreditSkipped] = useState(false);
  const [creditApplied, setCreditApplied] = useState(false);
  const [editSoNumber, setEditSoNumber] = useState<string | null>(null);
  const [editPiNumber, setEditPiNumber] = useState<string | null>(null);

  // Dynamically fetch actual next invoice number based on warehouse prefix when creating new
  useEffect(() => {
    if (editInvoice) return;
    let isMounted = true;
    async function fetchNextInvoiceNumber() {
      try {
        const res = await getNextDocumentNumber('SALES_INVOICE', warehouseId || undefined);
        if (isMounted && res?.data?.nextNumber) {
          setInvoiceNo((prev) => {
            if (!prev || prev.startsWith('INV-') || prev.startsWith('DOC-')) {
              return res.data.nextNumber;
            }
            return prev;
          });
        }
      } catch (err) {
        console.warn('Failed to fetch next invoice number:', err);
      }
    }
    fetchNextInvoiceNumber();
    return () => {
      isMounted = false;
    };
  }, [warehouseId, editInvoice]);

  // Validation state
  const [overstockRows, setOverstockRows] = useState<Set<number>>(new Set());
  const [missingNarrationRows, setMissingNarrationRows] = useState<Set<number>>(new Set());
  const [showOverstockConfirm, setShowOverstockConfirm] = useState(false);
  const [overstockItems, setOverstockItems] = useState<OverstockItem[]>([]);
  const [pendingSaveAndNew, setPendingSaveAndNew] = useState(false);
  const [itemLookup, setItemLookup] = useState<ItemLookup[]>([]);
  const [categoryLookup, setCategoryLookup] = useState<CategoryLookup[]>([]);

  // const isSameState = isSameStateCheck(selectedCustomer?.stateCode ?? '', COMPANY_STATE);


  const isSameState = isSameStateCheck(selectedCustomer?.stateCode ?? '', companyStateCode);


  // ✅ Recalculate all items when isSameState changes (e.g. after companyStateCode loads)
  useEffect(() => {
    if (!companyStateCode) return; // wait until company state is loaded
    setItems((prev) =>
      prev.map((item) => {
        if (!item.itemId && !item.itemName) return item; // skip empty rows
        const taxable = round2(item.qty * item.rate * (1 - item.discount / 100));
        const gst = calcGST(item.taxRate, taxable, isSameState);
        return {
          ...item,
          taxableAmount: taxable,
          cgst: gst.cgst,
          sgst: gst.sgst,
          igst: gst.igst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
        };
      })
    );
  }, [isSameState, companyStateCode]);

  const totals = useMemo(() => calcTotals(items), [items]);

  // TDS is informational only — the seller never deducts it, the customer deducts
  // it from their side when paying (Section 194-series). This never touches
  // totals.grandTotal; it's purely a live preview of the server's own calc (the
  // authoritative amount is recomputed and persisted server-side on save).
  const tdsInfo = useMemo(() => {
    if (!selectedCustomer?.isTdsApplicable) {
      return { applicable: false, section: '', rate: 0, amount: 0, expectedReceipt: totals.grandTotal, hasPan: false, noPanWarning: false };
    }
    const pan = (selectedCustomer.pan || '').trim().toUpperCase();
    const hasPan = pan.length === 10;
    const baseRate = Number(selectedCustomer.tdsRate ?? 0);
    // Section 206AA — no PAN on record doubles the rate, floored at 20%.
    const effectiveRate = hasPan ? baseRate : Math.max(baseRate * 2, 20);
    const amount = Math.round(totals.taxableAmount * effectiveRate) / 100;
    return {
      applicable: true,
      section: selectedCustomer.tdsSection || '',
      rate: effectiveRate,
      amount,
      expectedReceipt: Number((totals.grandTotal - amount).toFixed(2)),
      hasPan,
      noPanWarning: !hasPan,
    };
  }, [selectedCustomer?.isTdsApplicable, selectedCustomer?.pan, selectedCustomer?.tdsRate, selectedCustomer?.tdsSection, totals.taxableAmount, totals.grandTotal]);

  // TCS (Section 206C(1H)) — unlike TDS, this IS added on top of the invoice total;
  // the customer pays extra, we collect it and deposit it to the government. Live
  // preview only — the server recomputes and persists its own authoritative figure,
  // including which of BELOW/CROSSING/ABOVE applies, from a row-locked running total.
  const [tcsSettings, setTcsSettings] = useState<{
    enableTcs: boolean; tcsThreshold: number; tcsRate: number; tcsRateWithoutPan: number;
  } | null>(null);
  const [customerAnnualSales, setCustomerAnnualSales] = useState<{
    totalTaxableAmount: number; tcsThresholdCrossed: boolean;
  } | null>(null);

  useEffect(() => {
    complianceApi.getTdsTcsSettings().then((res) => {
      if (res.success && res.data) {
        setTcsSettings({
          enableTcs: res.data.enableTcs,
          tcsThreshold: res.data.tcsThreshold,
          tcsRate: res.data.tcsRate,
          tcsRateWithoutPan: res.data.tcsRateWithoutPan,
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCustomer?.id) { setCustomerAnnualSales(null); return; }
    let cancelled = false;
    const fy = getFinancialYear(date);
    complianceApi.getCustomerAnnualSales({ customerId: selectedCustomer.id, financialYear: fy }).then((res) => {
      if (cancelled || !res.success || !res.data || Array.isArray(res.data)) return;
      setCustomerAnnualSales({
        totalTaxableAmount: Number(res.data.totalTaxableAmount || 0),
        tcsThresholdCrossed: Boolean(res.data.tcsThresholdCrossed),
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedCustomer?.id, date]);

  const tcsInfo = useMemo(() => {
    const threshold = tcsSettings?.tcsThreshold ?? 5000000;
    if (!tcsSettings?.enableTcs || !selectedCustomer) {
      return { applicable: false, scenario: 'BELOW' as const, rate: 0, taxableForTcs: 0, amount: 0, runningTotal: 0, threshold, hasPan: false };
    }
    const pan = (selectedCustomer.pan || '').trim().toUpperCase();
    const hasPan = pan.length === 10;
    const rate = hasPan ? tcsSettings.tcsRate : tcsSettings.tcsRateWithoutPan;
    const runningTotal = customerAnnualSales?.totalTaxableAmount ?? 0;
    const alreadyCrossed = customerAnnualSales?.tcsThresholdCrossed ?? false;

    let taxableForTcs = 0;
    let scenario: 'BELOW' | 'CROSSING' | 'ABOVE' = 'BELOW';
    if (alreadyCrossed || runningTotal >= threshold) {
      taxableForTcs = totals.taxableAmount;
      scenario = 'ABOVE';
    } else if (runningTotal + totals.taxableAmount > threshold) {
      taxableForTcs = (runningTotal + totals.taxableAmount) - threshold;
      scenario = 'CROSSING';
    }
    const amount = Math.round(taxableForTcs * rate) / 100;
    return { applicable: amount > 0, scenario, rate, taxableForTcs, amount, runningTotal, threshold, hasPan };
  }, [tcsSettings, selectedCustomer, customerAnnualSales, totals.taxableAmount]);

  // Ledger convention: positive balance = customer owes us, negative = the
  // customer has credit with us (advances / return credits). Usable credit is
  // exactly the negative part — the credit LIMIT is headroom, not money.
  const customerBalance = Number(selectedCustomer?.balance ?? selectedCustomer?.openingBalance ?? 0) || 0;
  const customerCreditLimit = Number(selectedCustomer?.creditLimit ?? 0) || 0;
  // TCS is real money added on top — what the customer actually owes includes it.
  const invoiceTotalWithTcs = Number((totals.grandTotal + tcsInfo.amount).toFixed(2));
  const availableCreditAdjustment = Math.max(0, -customerBalance);
  const appliedCustomerCredit = creditApplied ? Math.min(availableCreditAdjustment, invoiceTotalWithTcs) : 0;
  const netPayable = Math.max(0, invoiceTotalWithTcs - appliedCustomerCredit);

  const buildCurrentDraft = useCallback((): SalesInvoiceDraft => ({
    invoiceNo,
    date,
    dueDate,
    salesman,
    warehouseId,
    selectedCustomer: selectedCustomer
      ? {
        id: selectedCustomer.id,
        name: selectedCustomer.name,
        gstin: selectedCustomer.gstin,
        phone: selectedCustomer.phone,
        stateCode: selectedCustomer.stateCode,
        billingAddress: selectedCustomer.billingAddress,
        shippingAddress: selectedCustomer.shippingAddress,
        creditLimit: selectedCustomer.creditLimit,
        openingBalance: selectedCustomer.openingBalance,
        balance: selectedCustomer.balance,
      }
      : null,
    billingAddress,
    shippingAddress,
    payment: {
      cash: payment.cash,
      upi: payment.upi,
      card: payment.card,
      cheque: payment.cheque,
      chequeBankName: payment.chequeBankName,
      chequeNo: payment.chequeNo,
      chequeDate: payment.chequeDate,
      chequeBranch: payment.chequeBranch,
    },
    narration,
    items,
    creditSkipped,
    creditApplied,
  }), [
    invoiceNo,
    date,
    dueDate,
    salesman,
    warehouseId,
    selectedCustomer,
    billingAddress,
    shippingAddress,
    payment,
    narration,
    items,
    creditSkipped,
    creditApplied,
  ]);

  const buildItemsFromDuplicatePrefill = useCallback((prefill: NonNullable<typeof duplicatePrefill>): InvoiceLineItem[] => (
    prefill.items.map((di) => {
      const taxable = round2(di.qty * di.rate * (1 - (di.discount ?? 0) / 100));
      const gst = calcGST(di.taxRate ?? 18, taxable, isSameState);

      return {
        ...emptyLineItem(),
        id: crypto.randomUUID(),
        itemId: di.itemId ?? '',
        itemCode: '',
        itemName: di.itemName,
        hsnCode: di.hsnCode ?? '',
        qty: di.qty,
        unit: di.unit || 'Pcs',
        unitId: '',
        rate: di.rate,
        discount: di.discount ?? 0,
        taxRate: di.taxRate ?? 18,
        taxableAmount: taxable,
        cgst: gst.cgst,
        sgst: gst.sgst,
        igst: gst.igst,
        total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
        categoryId: '',
      };
    })
  ), []);

  const buildItemsFromChallanPrefill = useCallback(
    (prefill: NonNullable<typeof challanPrefill>): InvoiceLineItem[] =>
      prefill.items.map((ci) => {
        const qty = ci.qty > 0 ? ci.qty : 1;
        const rate = ci.rate || 0;
        const taxable = round2(qty * rate * (1 - (ci.discount || 0) / 100));
        const gst = calcGST(ci.taxRate || 18, taxable, isSameState);

        return {
          ...emptyLineItem(),
          id: crypto.randomUUID(),
          itemId: ci.itemId || '', // ✅ ADD — itemId use karo
          itemCode: ci.itemCode || '',
          itemName: ci.itemName,
          hsnCode: ci.hsnCode || '',
          qty,
          unit: ci.unit || 'Pcs',
          unitId: '',
          rate,
          discount: ci.discount || 0,
          taxRate: ci.taxRate || 18,
          taxableAmount: taxable,
          cgst: gst.cgst,
          sgst: gst.sgst,
          igst: gst.igst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
          categoryId: '',
        };
      }),
    [isSameState],
  );

  const buildItemsFromProformaPrefill = useCallback(
    (prefill: any): InvoiceLineItem[] =>
      (prefill.items || []).map((pi: any) => {
        // Carried over in the unit the proforma was made in
        const carried = carryLineUnit(pi);
        const qty = carried.qty > 0 ? carried.qty : 1;
        const rate = carried.rate || 0;
        const discount = pi.discount || pi.discountPercent || 0;
        const taxable = round2(qty * rate * (1 - discount / 100));
        const gst = calcGST(pi.taxRate || 18, taxable, isSameState);

        return {
          ...emptyLineItem(),
          id: crypto.randomUUID(),
          itemId: pi.itemId || '',
          itemCode: pi.itemCode || '',
          itemName: pi.itemName || '',
          hsnCode: pi.hsnCode || '',
          qty,
          unit: pi.unit || 'Pcs',
          unitId: pi.unitId || '',
          enteredUnitId: carried.enteredUnitId,
          rate,
          discount,
          taxRate: pi.taxRate || 18,
          taxableAmount: taxable,
          cgst: gst.cgst,
          sgst: gst.sgst,
          igst: gst.igst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
          categoryId: '',
        };
      }),
    [isSameState],
  );

  const buildItemsFromSalesOrderPrefill = useCallback(
    (prefill: any): InvoiceLineItem[] =>
      (prefill.items || []).map((soi: any) => {
        const qty = (soi.qty || soi.pendingQty) > 0 ? (soi.qty || soi.pendingQty) : 1;
        const rate = soi.rate || 0;
        const discount = soi.discount || 0;
        const taxable = round2(qty * rate * (1 - discount / 100));
        const gst = calcGST(soi.taxRate || 18, taxable, isSameState);

        return {
          ...emptyLineItem(),
          id: crypto.randomUUID(),
          soItemId: soi.soItemId || soi.id || '',
          itemId: soi.itemId || '',
          itemCode: soi.itemCode || '',
          itemName: soi.itemName || '',
          hsnCode: soi.hsnCode || '',
          qty,
          unit: soi.unit || 'Pcs',
          unitId: soi.unitId || '',
          rate,
          discount,
          taxRate: soi.taxRate || 18,
          taxableAmount: taxable,
          cgst: gst.cgst,
          sgst: gst.sgst,
          igst: gst.igst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
          categoryId: '',
        };
      }),
    [isSameState],
  );

  const buildItemsFromQuotationPrefill = useCallback(
    (prefill: any): InvoiceLineItem[] =>
      (prefill.items || []).map((qi: any) => {
        // Carried over in the unit the quotation was made in
        const carried = carryLineUnit(qi);
        const qty = carried.qty > 0 ? carried.qty : 1;
        const rate = carried.rate || 0;
        const discount = qi.discount || qi.discountPercent || 0;
        const taxable = round2(qty * rate * (1 - discount / 100));
        const gst = calcGST(qi.taxRate || 18, taxable, isSameState);

        return {
          ...emptyLineItem(),
          id: crypto.randomUUID(),
          itemId: qi.itemId || '',
          itemCode: qi.itemCode || '',
          itemName: qi.itemName || '',
          hsnCode: qi.hsnCode || '',
          qty,
          unit: qi.unit || 'Pcs',
          unitId: qi.unitId || '',
          enteredUnitId: carried.enteredUnitId,
          rate,
          discount,
          taxRate: qi.taxRate || 18,
          taxableAmount: taxable,
          cgst: gst.cgst,
          sgst: gst.sgst,
          igst: gst.igst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
          categoryId: '',
        };
      }),
    [isSameState],
  );

  const buildItemsFromEditInvoice = useCallback((invoice: EditInvoiceState): InvoiceLineItem[] => (
    (invoice.items ?? []).map((item) => ({
      ...emptyLineItem(),
      id: item.id || crypto.randomUUID(),
      itemId: item.itemId || '',
      itemCode: item.itemCode || '',
      itemName: item.itemName || '',
      hsnCode: item.hsnCode || '',
      // As entered when another unit than the stock unit was used (2 Ton @ ₹80,000)
      ...(item.enteredUnitId && item.enteredQty != null
        ? { qty: Number(item.enteredQty), rate: Number(item.enteredRate ?? item.rate ?? 0), enteredUnitId: item.enteredUnitId }
        : { qty: item.qty > 0 ? item.qty : 1, rate: item.rate || 0, enteredUnitId: null }),
      unit: item.unit || 'Pcs',
      unitId: item.unitId || '',
      discount: item.discount || 0,
      taxRate: item.taxRate || 18,
      taxableAmount: item.taxableAmount || 0,
      cgst: item.cgst || 0,
      sgst: item.sgst || 0,
      igst: item.igst || 0,
      total: item.total || 0,
      categoryId: item.categoryId || '',
      narration: item.narration,
      currentStock: item.currentStock,
    }))
  ), []);

  const applyDraftToState = useCallback((draft: SalesInvoiceDraft) => {
    setInvoiceNo(draft?.invoiceNo || nextTempInvoiceNo());
    setDate(draft?.date || new Date().toISOString().split('T')[0]);
    setDueDate(draft?.dueDate || '');
    setSalesman(draft.salesman || '');
    setSelectedCustomer(draft.selectedCustomer ? {
      id: draft.selectedCustomer.id,
      name: draft.selectedCustomer.name,
      gstin: draft.selectedCustomer.gstin,
      phone: draft.selectedCustomer.phone,
      stateCode: draft.selectedCustomer.stateCode,
      billingAddress: draft.selectedCustomer.billingAddress,
      shippingAddress: draft.selectedCustomer.shippingAddress,
      creditLimit: draft.selectedCustomer.creditLimit,
      openingBalance: draft.selectedCustomer.openingBalance,
      balance: draft.selectedCustomer.balance,
    } : null);
    setBillingAddress(draft.billingAddress || '');
    setShippingAddress(draft.shippingAddress || '');
    setPayment(draft.payment);
    setNarration(draft.narration || '');
    setItems(draft.items.length > 0 ? draft.items : [emptyLineItem()]);
    setCreditSkipped(Boolean(draft.creditSkipped));
    setCreditApplied(Boolean(draft.creditApplied));
    setQuickAddIdx(null);
    setPrintOpen(false);
    setSaving(false);
    setOverstockRows(new Set());
    setMissingNarrationRows(new Set());
    setShowOverstockConfirm(false);
    setOverstockItems([]);
    setPendingSaveAndNew(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const [itemsResult, categoriesResult] = await Promise.allSettled([
        filterItems({}),
        categoryService.list(),
      ]);

      if (!mounted) return;

      if (itemsResult.status === 'fulfilled') {
        setItemLookup((itemsResult.value.data ?? []).map((row: ItemResponse) => {
          const item = mapApiToItem(row);
          return {
            id: item.id,
            name: item.name,
            code: item.code,
            hsnCode: item.hsnCode,
            taxRate: item.taxRate,
            purchaseRate: item.purchaseRate,
            saleRate: item.saleRate,
            unit: item.unitName || 'Pcs',
            unitId: item.unitId,
            categoryId: item.categoryId || '',
            isBatchTracked: Boolean(item.isBatchTracked),
            isSerialTracked: Boolean(item.isSerialTracked),
          };
        }));
      } else {
        setItemLookup([]);
      }

      if (categoriesResult.status === 'fulfilled') {
        setCategoryLookup((categoriesResult.value as Array<{ id: string; name: string; requires_narration?: boolean }>).map((row) => ({
          id: row.id,
          name: row.name,
          requiresNarration: Boolean(row.requires_narration),
        })));
      } else {
        setCategoryLookup([]);
      }



    })();
    return () => {
      mounted = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (!activeTab) return;
    hydratingRef.current = true;
    const initialPaymentBreakdown = (editInvoice as any)?.paymentBreakdown
      ? {
          cash: Number((editInvoice as any).paymentBreakdown.cash || 0),
          upi: Number((editInvoice as any).paymentBreakdown.upi || 0),
          card: Number((editInvoice as any).paymentBreakdown.card || 0),
          cheque: Number((editInvoice as any).paymentBreakdown.cheque || 0),
          chequeBankName: (editInvoice as any).paymentBreakdown.chequeBankName || '',
          chequeNo: (editInvoice as any).paymentBreakdown.chequeNo || '',
          chequeDate: (editInvoice as any).paymentBreakdown.chequeDate || '',
          chequeBranch: (editInvoice as any).paymentBreakdown.chequeBranch || '',
        }
      : editInvoice?.paidAmount
        ? {
            cash: editInvoice.paymentMode === 'CASH' || !editInvoice.paymentMode ? Number(editInvoice.paidAmount) : 0,
            upi: editInvoice.paymentMode === 'UPI' ? Number(editInvoice.paidAmount) : 0,
            card: editInvoice.paymentMode === 'CARD' ? Number(editInvoice.paidAmount) : 0,
            cheque: editInvoice.paymentMode === 'CHEQUE' ? Number(editInvoice.paidAmount) : 0,
            chequeBankName: '', chequeNo: '', chequeDate: '', chequeBranch: '',
          }
        : activeDraft.payment;

    const nextDraft = editInvoice
      ? {
        ...activeDraft,
        invoiceNo: editInvoice.invoiceNo,
        date: editInvoice.date,
        dueDate: editInvoice.date,
        warehouseId: editInvoice.warehouseId || warehouseId,
        narration: editInvoice.notes || activeDraft.narration || '',
        payment: initialPaymentBreakdown,
        selectedCustomer: {
          id: editInvoice.customerId || '',
          name: editInvoice.partyName || (editInvoice as any).customerName || '',
          gstin: editInvoice.customerGstin || (editInvoice as any).customerGSTIN || '',
          phone: '',
          stateCode: companyStateCode,
          billingAddress: editInvoice.billingAddress || '',
          shippingAddress:
            editInvoice.shippingAddress || editInvoice.billingAddress || '',
        },
        billingAddress: editInvoice.billingAddress || '',
        shippingAddress:
          editInvoice.shippingAddress || editInvoice.billingAddress || '',
        items: buildItemsFromEditInvoice(editInvoice),
      }
      : fromSalesOrderPrefill
        ? {
          ...activeDraft,
          selectedCustomer: {
            id: fromSalesOrderPrefill.customerId || '',
            name: fromSalesOrderPrefill.customerName || '',
            gstin: fromSalesOrderPrefill.customerGSTIN || '',
            phone: '',
            stateCode: companyStateCode,
            billingAddress: fromSalesOrderPrefill.billingAddress || '',
            shippingAddress: fromSalesOrderPrefill.shippingAddress || '',
          },
          warehouseId: fromSalesOrderPrefill.warehouseId || warehouseId,
          billingAddress: fromSalesOrderPrefill.billingAddress || '',
          shippingAddress: fromSalesOrderPrefill.shippingAddress || '',
          items: buildItemsFromSalesOrderPrefill(fromSalesOrderPrefill),
        }
        : fromProformaPrefill
          ? {
            ...activeDraft,
            selectedCustomer: {
              id: fromProformaPrefill.customerId || '',
              name: fromProformaPrefill.customerName || '',
              gstin: fromProformaPrefill.customerGSTIN || '',
              phone: '',
              stateCode: companyStateCode,
              billingAddress: fromProformaPrefill.billingAddress || '',
              shippingAddress: fromProformaPrefill.shippingAddress || '',
            },
            warehouseId: fromProformaPrefill.warehouseId || warehouseId,
            billingAddress: fromProformaPrefill.billingAddress || '',
            shippingAddress: fromProformaPrefill.shippingAddress || '',
            items: buildItemsFromProformaPrefill(fromProformaPrefill),
          }
          : fromQuotationPrefill
            ? {
              ...activeDraft,
              selectedCustomer: {
                id: fromQuotationPrefill.customerId || '',
                name: fromQuotationPrefill.customerName || '',
                gstin: fromQuotationPrefill.customerGSTIN || '',
                phone: '',
                stateCode: companyStateCode,
                billingAddress: fromQuotationPrefill.billingAddress || '',
                shippingAddress: fromQuotationPrefill.shippingAddress || '',
              },
              warehouseId: fromQuotationPrefill.warehouseId || warehouseId,
              billingAddress: fromQuotationPrefill.billingAddress || '',
              shippingAddress: fromQuotationPrefill.shippingAddress || '',
              items: buildItemsFromQuotationPrefill(fromQuotationPrefill),
            }
            : activeDraft.items.length > 0
              ? activeDraft
              : duplicatePrefill
                ? {
                  ...activeDraft,
                  selectedCustomer: {
                    id: duplicatePrefill.partyId,
                    name: duplicatePrefill.partyName,
                    gstin: '',
                    phone: '',
                    stateCode: companyStateCode,
                    billingAddress: '',
                    shippingAddress: '',
                  },
                  items: buildItemsFromDuplicatePrefill(duplicatePrefill),
                }
                : challanPrefill
                  ? {
                    ...activeDraft,
                    selectedCustomer: {
                      id: challanPrefill.customerId || '',
                      name: challanPrefill.customerName,
                      gstin: '',
                      phone: '',
                      stateCode: companyStateCode,
                      billingAddress: challanPrefill.billingAddress || '',
                      shippingAddress: challanPrefill.shippingAddress || '',
                    },
                    warehouseId: challanPrefill.warehouseId || warehouseId,
                    billingAddress: challanPrefill.billingAddress || '',
                    shippingAddress: challanPrefill.shippingAddress || '',
                    items: buildItemsFromChallanPrefill(challanPrefill),
                  }
                  : activeDraft;
    applyDraftToState(nextDraft);
  }, [activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeTabId) return;
    // After a successful save we clear state and navigate away — don't let
    // this effect overwrite the now-empty store draft with stale local state.
    if (savedAndNavigatingRef.current) return;
    if (hydratingRef.current) {
      hydratingRef.current = false;
      return;
    }
    setTabDraft(activeTabId, buildCurrentDraft(), true);
  }, [
    activeTabId,
    invoiceNo,
    date,
    dueDate,
    salesman,
    warehouseId,
    selectedCustomer,
    billingAddress,
    shippingAddress,
    payment,
    narration,
    items,
    creditSkipped,
    creditApplied,
    setTabDraft,
    buildCurrentDraft,
  ]);

  useEffect(() => {
    const custId = fromSalesOrderPrefill?.customerId || fromProformaPrefill?.customerId || fromQuotationPrefill?.customerId;
    const refKey = fromSalesOrderPrefill?.soId || fromProformaPrefill?.proformaId || fromQuotationPrefill?.quotationId;
    if (!custId || !refKey) {
      orderCustomerHydratedRef.current = null;
      return;
    }
    if (orderCustomerHydratedRef.current === refKey) return;

    const hydrateOrderCustomer = async () => {
      try {
        const response = await getPartyById(custId);
        const matched = response.data;
        if (!matched) return;

        const sourceCustName = fromProformaPrefill?.customerName || (fromProformaPrefill as any)?.customer_name || fromSalesOrderPrefill?.customerName || fromQuotationPrefill?.customerName;
        const enrichedCustomer = {
          id: matched.id,
          name: sourceCustName || matched.name,
          gstin: matched.gstin ?? selectedCustomer?.gstin ?? '',
          phone: matched.phone ?? '',
          stateCode: matched.state_code ?? selectedCustomer?.stateCode ?? companyStateCode,
          billingAddress: matched.billing_address ?? selectedCustomer?.billingAddress ?? '',
          shippingAddress: matched.shipping_address ?? matched.billing_address ?? selectedCustomer?.shippingAddress ?? '',
          creditLimit: Number(matched.credit_limit ?? 0),
          openingBalance: Number(matched.opening_balance ?? 0),
          balance: Number((matched as unknown as { balance?: number }).balance ?? matched.opening_balance ?? 0),
        };

        orderCustomerHydratedRef.current = refKey;
        setSelectedCustomer(enrichedCustomer);
        setBillingAddress(enrichedCustomer.billingAddress);
        setShippingAddress(enrichedCustomer.shippingAddress);
      } catch {
        orderCustomerHydratedRef.current = refKey;
      }
    };

    void hydrateOrderCustomer();
  }, [fromSalesOrderPrefill?.customerId, fromProformaPrefill?.customerId, fromQuotationPrefill?.customerId, companyStateCode]);

  useEffect(() => {
    if (!duplicatePrefill?.partyId) {
      duplicateCustomerHydratedRef.current = null;
      return;
    }
    if (duplicateCustomerHydratedRef.current === duplicatePrefill.originalBillNo) return;

    const hydrateDuplicateCustomer = async () => {
      try {
        const response = await getPartyById(duplicatePrefill.partyId);
        const matched = response.data;
        if (!matched) return;

        const enrichedCustomer = {
          id: matched.id,
          name: matched.name,
          gstin: matched.gstin ?? '',
          phone: matched.phone ?? '',
          stateCode: matched.state_code ?? matched.stateCode ?? selectedCustomer?.stateCode ?? companyStateCode,
          billingAddress: matched.billing_address ?? matched.billingAddress ?? selectedCustomer?.billingAddress ?? '',
          shippingAddress: matched.shipping_address ?? matched.shippingAddress ?? matched.billing_address ?? selectedCustomer?.shippingAddress ?? '',
          creditLimit: Number(matched.credit_limit ?? 0),
          openingBalance: Number(matched.opening_balance ?? 0),
          balance: Number((matched as unknown as { balance?: number }).balance ?? matched.opening_balance ?? 0),
        };

        duplicateCustomerHydratedRef.current = duplicatePrefill.originalBillNo;
        setSelectedCustomer(enrichedCustomer);
        setBillingAddress(enrichedCustomer.billingAddress);
        setShippingAddress(enrichedCustomer.shippingAddress);
      } catch {
        duplicateCustomerHydratedRef.current = duplicatePrefill.originalBillNo;
      }
    };

    void hydrateDuplicateCustomer();
  }, [duplicatePrefill?.partyId, duplicatePrefill?.partyName]);

  useEffect(() => {
    if (!challanPrefill?.customerId) {
      challanCustomerHydratedRef.current = null;
      return;
    }
    if (challanCustomerHydratedRef.current === challanPrefill.challanId) return;

    const hydrateChallanCustomer = async () => {
      try {
        const response = await getPartyById(challanPrefill.customerId);
        const matched = response.data;
        if (!matched) return;

        const enrichedCustomer = {
          id: matched.id,
          name: matched.name,
          gstin: matched.gstin ?? '',
          phone: matched.phone ?? '',
          stateCode: matched.state_code ?? selectedCustomer?.stateCode ?? companyStateCode,
          billingAddress: matched.billing_address ?? challanPrefill.billingAddress ?? '',
          shippingAddress: matched.shipping_address ?? challanPrefill.shippingAddress ?? matched.billing_address ?? '',
          creditLimit: Number(matched.credit_limit ?? 0),
          openingBalance: Number(matched.opening_balance ?? 0),
          balance: Number((matched as unknown as { balance?: number }).balance ?? matched.opening_balance ?? 0),
        };

        challanCustomerHydratedRef.current = challanPrefill.challanId;
        setSelectedCustomer(enrichedCustomer);
        setBillingAddress(enrichedCustomer.billingAddress);
        setShippingAddress(enrichedCustomer.shippingAddress);
      } catch {
        challanCustomerHydratedRef.current = challanPrefill.challanId;
      }
    };

    void hydrateChallanCustomer();
  }, [challanPrefill?.customerId]);

  useEffect(() => {
    if (!editInvoice?.customerId) {
      editCustomerHydratedRef.current = null;
      return;
    }
    if (editCustomerHydratedRef.current === editInvoice.id) return;

    const hydrateEditCustomer = async () => {
      try {
        const response = await getPartyById(editInvoice.customerId);
        const matched = response.data;
        if (!matched) return;

        const enrichedCustomer = {
          id: matched.id,
          name: matched.name,
          gstin: matched.gstin ?? editInvoice.customerGstin ?? '',
          phone: matched.phone ?? '',
          stateCode: matched.state_code ?? selectedCustomer?.stateCode ?? companyStateCode,
          billingAddress: matched.billing_address ?? editInvoice.billingAddress ?? '',
          shippingAddress: matched.shipping_address ?? editInvoice.shippingAddress ?? editInvoice.billingAddress ?? '',
          creditLimit: Number(matched.credit_limit ?? 0),
          openingBalance: Number(matched.opening_balance ?? 0),
          balance: Number((matched as unknown as { balance?: number }).balance ?? matched.opening_balance ?? 0),
        };

        editCustomerHydratedRef.current = editInvoice.id;
        setSelectedCustomer(enrichedCustomer);
        setBillingAddress(enrichedCustomer.billingAddress);
        setShippingAddress(enrichedCustomer.shippingAddress);
      } catch {
        editCustomerHydratedRef.current = editInvoice.id;
      }
    };

    void hydrateEditCustomer();
  }, [companyStateCode, editInvoice?.billingAddress, editInvoice?.customerGstin, editInvoice?.customerId, editInvoice?.shippingAddress, selectedCustomer?.stateCode]);

  const editInvoiceDetailHydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editInvoice?.id) {
      editInvoiceDetailHydratedRef.current = null;
      return;
    }
    if (editInvoiceDetailHydratedRef.current === editInvoice.id) return;

    const hydrateEditInvoiceDetails = async () => {
      try {
        const fullInvoice = await salesService.getInvoice(editInvoice.id);
        if (!fullInvoice) return;
        editInvoiceDetailHydratedRef.current = editInvoice.id;

        const so = fullInvoice.soNumber || fullInvoice.soReference || (fullInvoice as any).salesOrderNumber || (fullInvoice as any).so_reference;
        const pi = fullInvoice.piNumber || fullInvoice.proformaNumber || (fullInvoice as any).proforma_number;
        if (so) setEditSoNumber(so);
        if (pi) setEditPiNumber(pi);

        if (Array.isArray(fullInvoice.items) && fullInvoice.items.length > 0) {
          setItems(buildItemsFromEditInvoice(fullInvoice as any));
        }
        if (fullInvoice.notes) setNarration(fullInvoice.notes);
        if (fullInvoice.date) setDate(fullInvoice.date.split('T')[0]);

        if ((fullInvoice as any).paymentBreakdown) {
          const pb = (fullInvoice as any).paymentBreakdown;
          setPayment({
            cash: Number(pb.cash || 0),
            upi: Number(pb.upi || 0),
            card: Number(pb.card || 0),
            cheque: Number(pb.cheque || 0),
            chequeBankName: pb.chequeBankName || '',
            chequeNo: pb.chequeNo || '',
            chequeDate: pb.chequeDate || '',
            chequeBranch: pb.chequeBranch || '',
          });
        } else if (fullInvoice.paidAmount) {
          const mode = fullInvoice.paymentMode || 'CASH';
          setPayment({
            cash: mode === 'CASH' ? Number(fullInvoice.paidAmount) : 0,
            upi: mode === 'UPI' ? Number(fullInvoice.paidAmount) : 0,
            card: mode === 'CARD' ? Number(fullInvoice.paidAmount) : 0,
            cheque: mode === 'CHEQUE' ? Number(fullInvoice.paidAmount) : 0,
            chequeBankName: '', chequeNo: '', chequeDate: '', chequeBranch: '',
          });
        }
      } catch (err) {
        console.error('Failed to load full invoice details for edit:', err);
        editInvoiceDetailHydratedRef.current = editInvoice.id;
      }
    };

    void hydrateEditInvoiceDetails();
  }, [editInvoice?.id]);

  const resolveItemIdentity = useCallback((row: InvoiceLineItem): InvoiceLineItem => {
    let found = row.itemId ? itemLookup.find((s) => s.id === row.itemId) : undefined;
    if (!found) {
      const qName = row.itemName.trim().toLowerCase();
      const qCode = row.itemCode.trim().toLowerCase();
      found = itemLookup.find((s) => {
        const dbName = s.name.trim().toLowerCase();
        const dbCode = s.code.trim().toLowerCase();
        const byName = qName && (dbName === qName || dbName.includes(qName) || qName.includes(dbName));
        const byCode = qCode && s.code.trim().toLowerCase() === qCode;
        // Users often type exact item code in the searchable item field (row.itemName).
        const byTypedCodeInName = qName && dbCode === qName;
        return byName || byCode || byTypedCodeInName;
      });
    }
    if (!found) return row;

    const patched = {
      ...row,
      itemId: found.id,
      itemCode: row.itemCode || found.code,
      itemName: row.itemName || found.name,
      hsnCode: row.hsnCode || found.hsnCode,
      unit: row.unit || found.unit,
      unitId: row.unitId || found.unitId,
      taxRate: row.taxRate || found.taxRate,
      rate: row.rate || found.saleRate,
      categoryId: row.categoryId || found.categoryId || '',
      isBatchTracked: found.isBatchTracked ?? row.isBatchTracked ?? false,
      isSerialTracked: found.isSerialTracked ?? row.isSerialTracked ?? false,
      batchNumber: row.batchNumber ?? null,
      serialNumbers: row.serialNumbers ?? '',
    };
    const taxable = round2(patched.qty * patched.rate * (1 - patched.discount / 100));
    const gst = calcGST(patched.taxRate, taxable, isSameState);
    return { ...patched, taxableAmount: taxable, ...gst, total: round2(taxable + gst.cgst + gst.sgst + gst.igst) };
  }, [isSameState, itemLookup]);

  const prefilledItemsHydratedRef = useRef(false);
  useEffect(() => {
    if (itemLookup.length === 0 || prefilledItemsHydratedRef.current) return;

    setItems((prev) => {
      const hasInvoiceRows = prev.some((row) => row.itemName || row.itemCode);
      if (!hasInvoiceRows) return prev;

      prefilledItemsHydratedRef.current = true;
      return prev.map(resolveItemIdentity);
    });
  }, [itemLookup.length, resolveItemIdentity]);

  // Check if user can override stock (SUPER_ADMIN or SUB_ADMIN)
  const canOverrideStock = user?.role === 'SUPER_ADMIN' || user?.role === 'SUB_ADMIN';

  const handleCustomerSelect = useCallback((c: CustomerInfo) => {
    setSelectedCustomer(c);
    setBillingAddress(c.billingAddress);
    setShippingAddress(c.shippingAddress);
    setCreditSkipped(false);
    setCreditApplied(false);
  }, []);

  const persistCurrentDraft = useCallback(() => {
    if (!activeTabId) return;
    setTabDraft(activeTabId, buildCurrentDraft(), true);
  }, [activeTabId, buildCurrentDraft, setTabDraft]);

  const handleSelectTab = useCallback((tabId: string) => {
    persistCurrentDraft();
    setActiveTab(tabId);
  }, [persistCurrentDraft, setActiveTab]);

  const handleNewTab = useCallback(() => {
    persistCurrentDraft();
    addTab('SALE');
  }, [addTab, persistCurrentDraft]);

  const handleCloseTab = useCallback((tabId: string, isLastVisibleTab: boolean) => {
    if (tabId === activeTabId) {
      persistCurrentDraft();
    }
    closeTab(tabId);
    if (isLastVisibleTab) {
      navigate('/sales/invoices');
    }
  }, [activeTabId, closeTab, navigate, persistCurrentDraft]);

  const doSave = useCallback(async (andNew: boolean) => {
    const normalizedItems = items.map(resolveItemIdentity);
    const validItems = normalizedItems.filter((i) => i.itemId && i.qty > 0);
    if (validItems.length === 0) {
      console.table(normalizedItems.map((i) => ({
        itemName: i.itemName,
        itemCode: i.itemCode,
        itemId: i.itemId,
        qty: i.qty,
        rate: i.rate,
      })));
      toast.error('Add at least one item');
      return;
    }

    if (normalizedItems !== items) {
      setItems(normalizedItems);
    }

    setSaving(true);
    try {
      let savedInvoiceNo = invoiceNo;
      if (isBackendAvailable()) {
        const totalPaid = payment.cash + payment.upi + payment.card + payment.cheque;
        const paymentModesUsed = [
          payment.cash > 0 ? 'CASH' : null,
          payment.upi > 0 ? 'UPI' : null,
          payment.card > 0 ? 'CARD' : null,
          payment.cheque > 0 ? 'CHEQUE' : null,
          appliedCustomerCredit > 0 ? 'CREDIT_AMOUNT' : null,
        ].filter(Boolean) as string[];

        const paymentMode = paymentModesUsed.length === 1
          ? paymentModesUsed[0]
          : paymentModesUsed.length > 1
            ? 'MIXED'
            : 'CREDIT';

        const proformaId = fromProformaPrefill?.proformaInvoiceId || fromProformaPrefill?.proformaId || fromProformaPrefill?.id || editInvoice?.proformaInvoiceId || (editInvoice as any)?.proformaId;
        const salesOrderId = fromSalesOrderPrefill?.salesOrderId || fromSalesOrderPrefill?.soId || fromSalesOrderPrefill?.id || editInvoice?.salesOrderId || (editInvoice as any)?.soId;
        const soRef = fromSalesOrderPrefill?.soNumber || fromSalesOrderPrefill?.so_number || fromSalesOrderPrefill?.salesOrderNumber || fromSalesOrderPrefill?.soReference || editInvoice?.soNumber || editInvoice?.soReference || (editInvoice as any)?.salesOrderNumber || editSoNumber;
        const poRef = fromSalesOrderPrefill?.customerPoNumber || fromSalesOrderPrefill?.customer_po_number || editInvoice?.customerPoReference;
        const delDate = fromSalesOrderPrefill?.deliveryDate || fromSalesOrderPrefill?.delivery_date || editInvoice?.deliveryDate;

        const payload = {
          invoiceNo: invoiceNo ? invoiceNo.trim() : undefined,
          invoiceNumber: invoiceNo ? invoiceNo.trim() : undefined,
          customerId: selectedCustomer!.id, warehouseId, date, billingAddress, shippingAddress,
          paymentMode,
          isSameState,
          amountReceived: totalPaid > 0 ? totalPaid : undefined,
          useCustomerCredit: Boolean(creditApplied),
          proformaInvoiceId: proformaId || undefined,
          salesOrderId: salesOrderId || undefined,
          soReference: soRef || undefined,
          customerPoReference: poRef || undefined,
          deliveryDate: delDate || undefined,
          paymentBreakdown: {
            cash: payment.cash,
            upi: payment.upi,
            card: payment.card,
            cheque: payment.cheque,
            chequeBankName: payment.chequeBankName,
            chequeNo: payment.chequeNo,
            chequeDate: payment.chequeDate,
            chequeBranch: payment.chequeBranch,
          },
          notes: narration,
          items: validItems.map((i) => ({
            itemId: i.itemId,
            itemCode: i.itemCode || '',
            itemName: i.itemName || '',
            soItemId: (i as any).soItemId || undefined,
            qty: i.qty,
            unit: i.unit || 'Pcs',
            unitId: i.unitId || undefined,
            // qty and rate are in this unit; the server converts them to the stock unit
            enteredUnitId: i.enteredUnitId || undefined,
            rate: i.rate,
            discount: i.discount,
            taxRate: i.taxRate,
            hsnCode: i.hsnCode,
            categoryId: i.categoryId || undefined,
            narration: i.narration || undefined,
            batchNumber: i.batchNumber || undefined,
            batchId: i.batchId || undefined,
            mfgDate: i.mfgDate || undefined,
            expiryDate: i.expiryDate || undefined,
            autoFIFO: i.autoFIFO !== undefined ? i.autoFIFO : true,
            isSerialTracked: Boolean(i.isSerialTracked),
            serialIds: Array.isArray(i.serialIds) && i.serialIds.length > 0 ? i.serialIds : undefined,
            serialNumbers: i.serialNumbers
              ? (Array.isArray(i.serialNumbers) ? i.serialNumbers : i.serialNumbers.split(/[,\n]+/).map((s: string) => s.trim()).filter(Boolean))
              : undefined,
          })),
          challanId: challanPrefill?.challanId || undefined,
        };

        (window as unknown as { __lastCreateInvoicePayload?: unknown }).__lastCreateInvoicePayload = payload;
        
        let created;
        if (editInvoice?.id) {
          created = await salesService.updateInvoice(editInvoice.id, payload);
        } else {
          created = await salesService.createInvoice(payload);
        }

        if (created?.invoiceNo) {
          setInvoiceNo(created.invoiceNo);
          savedInvoiceNo = created.invoiceNo;
        }
      }
      if (activeTabId) markSaved(activeTabId, savedInvoiceNo);
      toast.success(`Invoice ${savedInvoiceNo} saved!`);
      if (andNew) {
        // Reset all local state for the next invoice (stay on page)
        setItems([emptyLineItem()]); setSelectedCustomer(null); setBillingAddress(''); setShippingAddress(''); setPayment(emptyPayment()); setNarration('');
        setOverstockRows(new Set()); setMissingNarrationRows(new Set());
        setCreditSkipped(false);
        setCreditApplied(false);
        setInvoiceNo(nextTempInvoiceNo());
        if (activeTabId) setTabDraft(activeTabId, createEmptySalesInvoiceDraft(), false);
      } else {
        // Block auto-save effect so it can't re-write stale local state to the store
        savedAndNavigatingRef.current = true;
        // Wipe the Zustand store draft to empty so the next "New Invoice" click
        // always opens a blank form instead of showing this saved invoice's data.
        if (activeTabId) setTabDraft(activeTabId, createEmptySalesInvoiceDraft(), false);
        navigate('/sales/invoices');
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to save invoice';
      toast.error(message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  }, [selectedCustomer, items, warehouseId, date, billingAddress, shippingAddress, payment, narration, totals, activeTabId, invoiceNo, navigate, toast, markSaved, setTabDraft, resolveItemIdentity, appliedCustomerCredit, creditApplied]);

  const handleSave = useCallback(async (andNew = false) => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return; }
    if (!warehouseId) { toast.error('Please select a warehouse'); return; }
    const normalizedItems = items.map(resolveItemIdentity);
    const validItems = normalizedItems.filter((i) => i.itemId && i.qty > 0);
    if (validItems.length === 0) {
      console.table(normalizedItems.map((i) => ({
        itemName: i.itemName,
        itemCode: i.itemCode,
        itemId: i.itemId,
        qty: i.qty,
        rate: i.rate,
      })));
      toast.error('Add at least one item (select item from dropdown or exact item code)');
      return;
    }

    if (normalizedItems !== items) {
      setItems(normalizedItems);
    }

    // ── Total Usable Credit Limit Validation ──
    const customerBal = Number(selectedCustomer?.balance ?? selectedCustomer?.openingBalance ?? 0) || 0;
    const customerLimit = Number(selectedCustomer?.creditLimit ?? 0) || 0;
    const usableCredit = Math.max(0, customerLimit - customerBal);
    const totalPaid = (payment.cash || 0) + (payment.upi || 0) + (payment.card || 0) + (payment.cheque || 0);
    const creditAmountNeeded = Math.max(0, invoiceTotalWithTcs - totalPaid);

    if (creditApplied && usableCredit <= 0) {
      toast.error('Customer has 0 total usable credit limit. Cannot apply customer credit.');
      return;
    }

    if (creditAmountNeeded > 0) {
      if (usableCredit <= 0) {
        toast.error('Customer has 0 total usable credit limit. Invoice cannot be created on credit.');
        return;
      }
      if (creditAmountNeeded > usableCredit) {
        toast.error(`Credit limit exceeded! Customer total usable credit is ${formatINR(usableCredit)}, but credit required is ${formatINR(creditAmountNeeded)}.`);
        return;
      }
    }

    const overstock: OverstockItem[] = [];
    const overstockIdxs = new Set<number>();
    normalizedItems.forEach((row, idx) => {
      if (!row.itemId || row.qty <= 0) return;
      const available = row.currentStock ?? 0;
      // Stock is in the item's unit: 2 Ton needs 2000 Kg
      const stockQty = row.qty * units.toStock(row.enteredUnitId, row.unit);
      if (stockQty > available + 1e-9) {
        overstock.push({ rowIdx: idx, itemName: row.itemName, entered: stockQty, available });
        overstockIdxs.add(idx);
      }
    });
    if (overstock.length > 0) {
      setOverstockRows(overstockIdxs);
      setOverstockItems(overstock);
      setPendingSaveAndNew(andNew);
      setShowOverstockConfirm(true);
      return;
    }
    setOverstockRows(new Set());

    // ── Batch & Serial Tracking validation ──
    for (const row of normalizedItems) {
      if (!row.itemId || row.qty <= 0) continue;
      if (row.isBatchTracked && row.autoFIFO === false && !row.batchNumber?.trim()) {
        toast.error(`Please select a batch for ${row.itemName}`);
        return;
      }
      if (row.isSerialTracked) {
        const serialCount = (row.serialNumbers || '')
          .split(/[,\n]+/)
          .map((s) => s.trim())
          .filter(Boolean).length;
        if (serialCount !== Number(row.qty)) {
          toast.error(`Serial count (${serialCount}) must match quantity (${row.qty}) for ${row.itemName}`);
          return;
        }
      }
    }

    // ── Feature 2: Stock validation ──
    await doSave(andNew);
  }, [selectedCustomer, warehouseId, items, doSave, toast, resolveItemIdentity, categoryLookup, invoiceTotalWithTcs, payment, units]);

  const handleOverstockConfirm = useCallback(async () => {
    setShowOverstockConfirm(false);
    setOverstockRows(new Set());
    setOverstockItems([]);
    await doSave(pendingSaveAndNew);
  }, [doSave, pendingSaveAndNew]);

  const handleOverstockCancel = useCallback(() => {
    setShowOverstockConfirm(false);
    // Keep red highlights so user can fix
  }, []);

  const handleQuickItemSaved = useCallback(async (data: ItemFormData) => {
    const created = await createItem({
      name: data.name,
      code: data.code,
      barcode: data.barcode,
      categoryId: data.categoryId,
      categoryName: data.categoryName,
      hsnCode: data.hsnCode,
      taxRate: data.taxRate,
      unitId: data.unitId,
      unitName: data.unitName,
      purchaseRate: data.purchaseRate,
      saleRate: data.saleRate,
      minStockLevel: data.minStockLevel,
      isActive: data.isActive,
      itemType: data.itemType || undefined,
      procurementType: data.procurementType || undefined,
    });

    if (!created.data) {
      throw new Error('Failed to create item');
    }

    const mapped = mapApiToItem(created.data);
    setItemLookup((prev) => [
      ...prev.filter((item) => item.id !== mapped.id),
      {
        id: mapped.id,
        name: mapped.name,
        code: mapped.code,
        hsnCode: mapped.hsnCode,
        taxRate: mapped.taxRate,
        purchaseRate: mapped.purchaseRate,
        saleRate: mapped.saleRate,
        unit: mapped.unitName || 'Pcs',
        unitId: mapped.unitId,
        categoryId: mapped.categoryId || '',
        isBatchTracked: mapped.isBatchTracked,
        isSerialTracked: mapped.isSerialTracked,
      },
    ]);

    if (quickAddIdx !== null) {
      setItems((prev) => prev.map((item, i) => {
        if (i !== quickAddIdx) return item;
        const rate = mapped.saleRate;
        const qty = item.qty > 0 ? item.qty : 1;
        const patched = {
          ...item,
          itemId: mapped.id,
          itemCode: mapped.code,
          itemName: mapped.name,
          hsnCode: mapped.hsnCode,
          taxRate: mapped.taxRate,
          qty,
          rate,
          unit: mapped.unitName,
          unitId: mapped.unitId,
          categoryId: mapped.categoryId || '',
          isBatchTracked: mapped.isBatchTracked,
          isSerialTracked: mapped.isSerialTracked,
        };
        const taxable = round2(patched.qty * patched.rate * (1 - patched.discount / 100));
        const gst = calcGST(patched.taxRate, taxable, isSameState);
        return { ...patched, taxableAmount: taxable, ...gst, total: round2(taxable + gst.cgst + gst.sgst + gst.igst) };
      }));
    }
    setQuickAddIdx(null);
    toast.success(`Item "${mapped.name}" added and selected`);
  }, [quickAddIdx, isSameState, toast]);

  const buildPrintData = () => ({
    invoiceNo, date, customerName: selectedCustomer?.name ?? '—', customerGstin: selectedCustomer?.gstin,
    warehouseName: selectedWarehouseName,
    warehouseAddress: selectedWarehouseAddress,
    warehouseGstin: selectedWarehouseGstin,
    billingAddress, shippingAddress, paymentMode: 'CASH' as const,
    items: items.filter((i) => i.itemName).map((i, idx) => ({
      sr: idx + 1,
      name: i.itemName,
      hsn: i.hsnCode,
      qty: i.qty,
      unit: i.unit,
      rate: i.rate,
      discount: i.discount,
      taxable: i.taxableAmount,
      taxRate: i.taxRate,
      taxAmt: i.cgst + i.sgst + i.igst,
      total: i.total,
      batchNumber: (i as any).batchNumber || (i as any).batch_number,
      mfgDate: (i as any).mfgDate,
      serials: (Array.isArray((i as any).serials) && (i as any).serials.length > 0)
        ? (i as any).serials
        : (Array.isArray((i as any).serialNumbers) && (i as any).serialNumbers.length > 0)
        ? (i as any).serialNumbers
        : (typeof (i as any).serialNumbers === 'string' && (i as any).serialNumbers.trim())
        ? (i as any).serialNumbers
        : (Array.isArray((i as any).selectedSerials) && (i as any).selectedSerials.length > 0)
        ? (i as any).selectedSerials
        : undefined,
    })),
    subtotal: totals.subtotal, totalDiscount: totals.totalDiscount, taxableAmount: totals.taxableAmount,
    cgst: totals.totalCGST, sgst: totals.totalSGST, igst: totals.totalIGST, roundOff: totals.roundOff, grandTotal: totals.grandTotal, isSameState, notes: narration,
    tdsApplicable: tdsInfo.applicable, tdsSection: tdsInfo.section, tdsRate: tdsInfo.rate,
    tdsAmount: tdsInfo.amount, expectedReceipt: tdsInfo.expectedReceipt,
  });

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        <BillingTabBar
          voucherType="SALE"
          onNewTab={handleNewTab}
          onSelectTab={handleSelectTab}
          onCloseTab={handleCloseTab}
        />

        <div className="flex-1 overflow-y-auto bg-[#f8fafc] pb-16">
          {/* Page header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3 bg-white border-b border-[#e2e8f0]">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-base font-bold text-[#1e293b]">{editInvoice ? 'Edit Sales Invoice' : 'New Sales Invoice'}</h1>
                <p className="text-xs text-slate-400">{invoiceNo}</p>
              </div>
              {selectedWarehouseGstin && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-200">
                  <i className="ri-government-line text-indigo-500 text-xs" />
                  <span className="text-[11px] font-mono font-semibold text-indigo-700">{selectedWarehouseGstin}</span>
                </div>
              )}
              {activeTab?.isDirty && (
                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Unsaved
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* <button onClick={() => handleSave(true)} disabled={saving}
                className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f1f5f9] disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors">
                <i className="ri-save-line text-xs" /> Submit &amp; New <kbd className="text-[10px] bg-[#e2e8f0] px-1 rounded ml-0.5">F8</kbd>
              </button> */}
              <button onClick={() => handleSave(false)} disabled={saving}
                className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors">
                {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : <><i className="ri-save-3-line" /> Submit <kbd className="text-[10px] bg-white/20 px-1 rounded ml-0.5">F9</kbd></>}
              </button>
            </div>
          </div>

          {/* ── SECTION 1: ITEMS TABLE ── */}
          <div className="px-5 pt-4">
            <div className="flex items-center justify-between mb-1.5 px-1">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Items</span>
              <span className="text-xs text-slate-400">
                <i className="ri-barcode-line mr-1" />
                Scan barcode or type · Enter / Arrow keys to navigate
              </span>
            </div>
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
              <InvoiceItemsTable
                items={items}
                onChange={setItems}
                isSameState={isSameState}
                warehouseId={warehouseId}
                rateType="sale"
                onAddNewItem={setQuickAddIdx}
                overstockRows={overstockRows}
                missingNarrationRows={missingNarrationRows}
                itemLookup={itemLookup}
              />
            </div>
          </div>

          {/* ── PART E: Customer Credit Banner ── */}
          {selectedCustomer && availableCreditAdjustment > 0 && !creditSkipped && !creditApplied && (
            <div className="px-5 pt-3">
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
                    <i className="ri-wallet-3-line text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      Credit Available: {formatINR(availableCreditAdjustment)}
                    </p>
                    <p className="text-xs text-amber-600">
                      Balance the customer holds with you (advances / return credits)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setCreditApplied(true)}
                    className="h-8 px-3 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    Apply to Invoice
                  </button>
                  <button
                    onClick={() => setCreditSkipped(true)}
                    className="h-8 px-3 rounded-lg border border-amber-300 text-amber-700 text-xs font-medium hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* {selectedCustomer && creditApplied && (
            <div className="px-5 pt-3">
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 shrink-0">
                    <i className="ri-check-double-line text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">
                      Credit Applied: -{formatINR(totalCustomerCreditAvailable)} · Net Payable: {formatINR(netPayable)}
                    </p>
                    <p className="text-xs text-emerald-600">Credit will be consumed when this invoice is saved</p>
                  </div>
                </div>
                <button
                  onClick={() => setCreditApplied(false)}
                  className="h-7 px-2.5 rounded-lg border border-emerald-300 text-emerald-700 text-xs font-medium hover:bg-emerald-100 cursor-pointer whitespace-nowrap"
                >
                  Remove
                </button>
              </div>
            </div>
          )} */}

          {/* ── GSTIN Status Warning — reads the party's already-verified status, no
              fresh live lookup per invoice (that lives on Party Master's Verify GSTIN) ── */}
          {selectedCustomer?.gstin && selectedCustomer.gstinStatus && selectedCustomer.gstinStatus !== 'Active' && (
            <div className="px-5 pt-3">
              <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 shrink-0">
                  <i className="ri-error-warning-line text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    Customer's GSTIN is {selectedCustomer.gstinStatus} on GSTN
                  </p>
                  <p className="text-xs text-red-600">
                    Re-verify or update this customer's GSTIN in Party Master before invoicing.
                  </p>
                </div>
              </div>
            </div>
          )}
          {selectedCustomer?.gstin && selectedCustomer.gstinVerified === undefined && (
            <div className="px-5 pt-3">
              <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
                  <i className="ri-shield-line text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Customer's GSTIN hasn't been verified with GSTN yet</p>
                  <p className="text-xs text-amber-600">
                    Use "Verify" on this customer's GSTIN field in Party Master to confirm it's active.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── TDS Applicable Banner — informational: customer deducts TDS on their
              side when paying, we never subtract it from the invoice total ── */}
          {tdsInfo.applicable && (
            <div className="px-5 pt-3">
              <div className={`rounded-lg p-4 border ${
                tdsInfo.noPanWarning ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`text-lg ${tdsInfo.noPanWarning ? 'ri-alert-line text-amber-600' : 'ri-file-list-3-line text-blue-600'}`} />
                  <span className={`font-semibold text-sm ${tdsInfo.noPanWarning ? 'text-amber-800' : 'text-blue-800'}`}>
                    TDS Applicable — Section {tdsInfo.section}
                    {tdsInfo.noPanWarning ? ' — NO PAN (Rate Doubled)' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Customer PAN:</span>
                    <span className="ml-2 font-medium">
                      {tdsInfo.hasPan
                        ? <>{selectedCustomer?.pan} <i className="ri-checkbox-circle-fill text-green-600" /></>
                        : <span className="text-red-600">NOT ON RECORD <i className="ri-close-circle-fill" /></span>}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">TDS Section:</span>
                    <span className="ml-2 font-medium">{tdsInfo.section}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">TDS Rate:</span>
                    <span className={`ml-2 font-medium ${tdsInfo.noPanWarning ? 'text-red-600' : 'text-gray-800'}`}>
                      {tdsInfo.rate}% on taxable amount{tdsInfo.noPanWarning ? ' (doubled — no PAN)' : ''}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Taxable Amount:</span>
                    <span className="ml-2 font-medium">{formatINR(totals.taxableAmount)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">TDS Amount:</span>
                    <span className="ml-2 font-bold text-blue-700">{formatINR(tdsInfo.amount)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Expected Receipt:</span>
                    <span className="ml-2 font-bold text-green-700">{formatINR(tdsInfo.expectedReceipt)}</span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-3 italic">
                  {selectedCustomer?.name} will deduct {formatINR(tdsInfo.amount)} before payment. You will receive{' '}
                  {formatINR(tdsInfo.expectedReceipt)} in bank. TDS credit of {formatINR(tdsInfo.amount)} claimable in your ITR.
                </p>

                {tdsInfo.noPanWarning && selectedCustomer && (
                  <button
                    type="button"
                    onClick={() => navigate('/masters/parties')}
                    className="mt-2 text-xs text-amber-700 underline cursor-pointer"
                  >
                    Update Customer PAN in Party Master to reduce TDS rate →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── TCS Applicable Banner — Section 206C(1H). Unlike TDS this money IS
              added to the invoice total: the customer pays extra, we collect it and
              deposit it to the government. Three states: below/crossing/above threshold ── */}
          {selectedCustomer && tcsSettings?.enableTcs && (tcsInfo.applicable || tcsInfo.scenario !== 'BELOW') && (
            <div className="px-5 pt-3">
              <div className="rounded-lg p-4 border bg-purple-50 border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-government-line text-lg text-purple-600" />
                  <span className="font-semibold text-sm text-purple-800">
                    TCS Applicable — Section 206C(1H)
                    {tcsInfo.scenario === 'CROSSING' ? ' (crossing threshold this invoice)' : tcsInfo.scenario === 'ABOVE' ? ' (threshold already crossed this FY)' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Customer PAN:</span>
                    <span className="ml-2 font-medium">
                      {tcsInfo.hasPan
                        ? <>{selectedCustomer.pan} <i className="ri-checkbox-circle-fill text-green-600" /></>
                        : <span className="text-red-600">NOT ON RECORD — rate doubled to {tcsSettings.tcsRateWithoutPan}%</span>}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">TCS Rate:</span>
                    <span className="ml-2 font-medium">{tcsInfo.rate}% on taxable amount</span>
                  </div>
                  <div>
                    <span className="text-gray-500">FY Total So Far:</span>
                    <span className="ml-2 font-medium">{formatINR(tcsInfo.runningTotal)} (of {formatINR(tcsInfo.threshold)} threshold)</span>
                  </div>
                  <div>
                    <span className="text-gray-500">TCS on This Invoice:</span>
                    <span className="ml-2 font-bold text-purple-700">{formatINR(tcsInfo.amount)}</span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-3 italic">
                  {tcsInfo.scenario === 'CROSSING' && `This invoice crosses the ₹${(tcsInfo.threshold / 100000).toFixed(0)}L threshold — TCS applies only to the excess of ${formatINR(tcsInfo.taxableForTcs)}.`}
                  {tcsInfo.scenario === 'ABOVE' && `Threshold already crossed this FY — TCS applies to the full invoice taxable amount.`}
                  {tcsInfo.scenario === 'BELOW' && `Below the ₹${(tcsInfo.threshold / 100000).toFixed(0)}L threshold — no TCS yet.`}
                  {' '}Grand Total below already includes this TCS amount.
                </p>
              </div>
            </div>
          )}

          {/* ── SECTION 2: BOTTOM 3-COLUMN ── */}
          <div className="px-5 pt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <CustomerSection
              selected={selectedCustomer}
              onSelect={handleCustomerSelect}
              onClear={() => {
                setSelectedCustomer(null);
                setBillingAddress('');
                setShippingAddress('');
              }}
              billingAddress={billingAddress}
              shippingAddress={shippingAddress}
              onBillingAddressChange={setBillingAddress}
              onShippingAddressChange={setShippingAddress}
            />
            <PaymentSection
              payment={payment}
              onChange={setPayment}
              invoiceTotal={totals.grandTotal}
              customerCreditLimit={customerCreditLimit}
              customerBalance={customerBalance}
              availableCreditAdjustment={availableCreditAdjustment}
              creditApplied={creditApplied}
              onApplyCreditAdjustment={() => setCreditApplied(true)}
              onRemoveCreditAdjustment={() => setCreditApplied(false)}
            />

            <InvoiceInfoSummary
              invoiceNo={invoiceNo}
              onInvoiceNoChange={setInvoiceNo}
              date={date} dueDate={dueDate} salesman={salesman}
              isSameState={isSameState} totals={totals}
              netPayable={netPayable}
              totalPaid={payment.cash + payment.upi + payment.card + payment.cheque}
              tdsInfo={tdsInfo}
              tcsInfo={tcsInfo}
              onDateChange={setDate} onDueDateChange={setDueDate} onSalesmanChange={setSalesman}
              challanPrefill={challanPrefill}
              soPrefill={
                fromSalesOrderPrefill
                  ? { soNumber: fromSalesOrderPrefill.soNumber || fromSalesOrderPrefill.so_number || fromSalesOrderPrefill.salesOrderNumber || fromSalesOrderPrefill.soReference }
                  : editInvoice?.soNumber || editInvoice?.soReference || (editInvoice as any)?.salesOrderNumber || (editInvoice as any)?.so_reference || editSoNumber
                    ? { soNumber: editInvoice?.soNumber || editInvoice?.soReference || (editInvoice as any)?.salesOrderNumber || (editInvoice as any)?.so_reference || editSoNumber! }
                    : null
              }
              piPrefill={
                fromProformaPrefill
                  ? { piNumber: fromProformaPrefill.piNumber || fromProformaPrefill.proformaNumber || (fromProformaPrefill as any).pi_number }
                  : editInvoice?.piNumber || editInvoice?.proformaNumber || (editInvoice as any)?.proforma_number || editPiNumber
                    ? { piNumber: editInvoice?.piNumber || editInvoice?.proformaNumber || (editInvoice as any)?.proforma_number || editPiNumber! }
                    : null
              }
            />
          </div>

          {/* ── SECTION 3: NARRATION BAR ── */}
          <div className="px-5 pt-4">
            <div className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 flex items-center gap-3">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide shrink-0">Narration</label>
              <input type="text" value={narration} onChange={(e) => setNarration(e.target.value)}
                placeholder="Optional invoice note..."
                className="flex-1 h-8 px-2 text-sm bg-transparent focus:outline-none text-[#1e293b] placeholder:text-slate-400" />
            </div>
          </div>
        </div>

        {/* Overstock confirm dialog */}
        <ConfirmDialog
          open={showOverstockConfirm}
          title="Some items exceed available stock"
          message={`${overstockItems.map((o) => `${o.itemName} — Entered: ${o.entered}, Available: ${o.available}`).join('\n')}\n\nThis will reduce stock below zero if you continue.`}
          confirmLabel="Yes, Save Anyway"
          cancelLabel="No, Fix Quantities"
          variant="warning"
          onConfirm={handleOverstockConfirm}
          onCancel={handleOverstockCancel}
        />

        {/* Print preview */}
        {printOpen && (
          <PrintSalesInvoice data={buildPrintData()} onClose={() => setPrintOpen(false)} onPrint={() => printSalesInvoice(buildPrintData())} />
        )}

        {/* Quick Add Item */}
        <ItemForm open={quickAddIdx !== null} initialData={{}} isEditing={false} onClose={() => setQuickAddIdx(null)} onSave={handleQuickItemSaved} />

        <ShortcutBar
          onSave={() => handleSave(false)} onSaveAndNew={() => handleSave(true)}
          onPrint={() => setPrintOpen(true)} onBack={() => navigate('/sales/invoices')}
          saving={saving} isDirty={activeTab?.isDirty}
        />
      </div>
    </AppLayout>
  );
}
